import type { PlayerInfo } from '@ic/engine';
import { persisted } from './store';

export interface AuthState { token: string | null; user: PlayerInfo | null; admin?: boolean; hasRecovery?: boolean }
export const auth = persisted<AuthState>('ic.auth', { token: null, user: null, admin: false, hasRecovery: false });

export async function api<T = unknown>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const { token } = auth.get();
  const res = await fetch('/api' + path, {
    method: opts.method ?? (opts.body ? 'POST' : 'GET'),
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error ?? res.statusText);
  return data as T;
}

interface SelfInfo { user: PlayerInfo; admin: boolean; hasRecovery: boolean }
interface SessionReply extends SelfInfo { token: string; recoveryCode?: string }

function signIn(r: SessionReply) {
  auth.set({ token: r.token, user: r.user, admin: r.admin, hasRecovery: r.hasRecovery });
  return r;
}

export async function login(username: string, password: string) {
  return signIn(await api<SessionReply>('/auth/login', { body: { username, password } }));
}

/** Create an account; with `recovery` the reply carries a one-time recovery code. */
export async function register(username: string, password: string, recovery: boolean) {
  return signIn(await api<SessionReply>('/auth/register', { body: { username, password, recovery } }));
}

/** Choose a new password with the recovery code. Returns the new recovery code. */
export async function recover(username: string, code: string, password: string) {
  return signIn(await api<SessionReply>('/auth/recover', { body: { username, code, password } }));
}

export async function newRecoveryCode(password: string): Promise<string> {
  const r = await api<{ recoveryCode: string }>('/me/recovery', { body: { password } });
  auth.set({ ...auth.get(), hasRecovery: true });
  return r.recoveryCode;
}

export async function removeRecoveryCode() {
  await api('/me/recovery', { method: 'DELETE' });
  auth.set({ ...auth.get(), hasRecovery: false });
}

/** Refresh the account info (admin flag, rating) from the server, if logged in. */
export async function refreshMe() {
  if (!auth.get().token) return;
  try {
    const r = await api<SelfInfo>('/me');
    auth.set({ ...auth.get(), user: r.user, admin: r.admin, hasRecovery: r.hasRecovery });
  } catch (e) {
    // An expired session: forget it so the player is not stuck half-logged-in.
    if (/log in/i.test((e as Error).message)) auth.set({ token: null, user: null, admin: false, hasRecovery: false });
  }
}

export function logout() {
  api('/auth/logout', { method: 'POST' }).catch(() => {});
  auth.set({ token: null, user: null, admin: false, hasRecovery: false });
}

/** Save the recovery code as a text file. */
export function downloadRecoveryCode(username: string, code: string) {
  const text = [
    'IllegalChess recovery code',
    '',
    `Username: ${username}`,
    `Recovery code: ${code}`,
    '',
    'Use it on illegalchess.me under "Forgot password?" to choose a new password.',
    'It works once; you get a new code right after using it. Keep this file private.',
    '',
  ].join('\r\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `illegalchess-recovery-${username}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
